import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Trophy, 
  MapPin,
  Shield,
  Search,
  Filter,
  ChevronRight,
  Calendar,
  Award,
  Target,
  Building,
  Star
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { teamsApi } from '../../services/api';
import { LoadingSpinner, EmptyStates, TeamCardSkeleton } from '../Common';

const ModernTeamsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedDivision, setSelectedDivision] = useState('all');

  // Test fetch on component mount
  React.useEffect(() => {
    console.log('ModernTeamsPage mounted, testing direct fetch...');
    fetch('/api/teams/teams/')
      .then(res => {
        console.log('Direct fetch response status:', res.status);
        return res.json();
      })
      .then(data => {
        console.log('Direct fetch data:', data);
        console.log('Direct fetch count:', data.count);
        console.log('Direct fetch results length:', data.results?.length);
      })
      .catch(err => console.error('Direct fetch error:', err));
  }, []);

  const { data: teams, isLoading, error } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      try {
        console.log('Starting teams fetch...');
        const data = await teamsApi.getAll();
        console.log('Teams API response:', data);
        console.log('Response data type:', typeof data);
        console.log('Response data is array?:', Array.isArray(data));
        console.log('Response data length:', data?.length);
        console.log('First team:', data?.[0]);
        return data;
      } catch (err) {
        console.error('Error fetching teams:', err);
        throw err;
      }
    },
  });

  console.log('Teams data after query:', teams, 'Is array?', Array.isArray(teams), 'Length:', teams?.length);
  const filteredTeams = Array.isArray(teams) ? teams.filter(team => {
    const matchesSearch =
      team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' ||
      team.sport?.name === selectedSport;
    const matchesDivision = selectedDivision === 'all' ||
      team.division?.name === selectedDivision;
    return matchesSearch && matchesSport && matchesDivision;
  }) : [];

  // Get unique sports and divisions from the teams data
  const sports = Array.isArray(teams) ? Array.from(new Set(teams.map(t => t.sport?.name).filter(Boolean))) : [];
  const divisions = Array.isArray(teams) ? Array.from(new Set(teams.map(t => t.division?.name).filter(Boolean))) : [];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Team Directory
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">ALL </span>
              <span className="neon-text">TEAMS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Explore {teams?.length || 0} teams across all major professional sports leagues
            </p>
          </div>


          {/* Filters */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search teams by name or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 sm:flex-none sm:min-w-[160px]">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.sort().map(sport => (
                      <option key={sport} value={sport}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-none sm:min-w-[160px]">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedDivision}
                    onChange={(e) => setSelectedDivision(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Divisions</option>
                    {divisions.map(division => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {teams?.length || 0}
              </div>
              <div className="text-sm text-gray-400">Total Teams</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Building className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {divisions.length}
              </div>
              <div className="text-sm text-gray-400">Divisions</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <MapPin className="w-8 h-8 text-warning mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {filteredTeams.length}
              </div>
              <div className="text-sm text-gray-400">Filtered Results</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Teams Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <TeamCardSkeleton count={9} />
        ) : error ? (
          <EmptyStates.LoadingError
            error={(error as Error).message || 'Failed to load teams data'}
            onRetry={() => window.location.reload()}
          />
        ) : filteredTeams.length === 0 ? (
          <EmptyStates.NoTeams />
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredTeams.map((team) => (
              <motion.div
                key={team.id}
                variants={item}
                whileHover={{ scale: 1.02 }}
                className="group"
              >
                <Link 
                  to={`/teams/${team.slug || team.id}`}
                  className="block h-full"
                >
                  <div 
                    className="glass-card h-full overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]"
                  >
                  {/* Main Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        {/* Team Logo Placeholder */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-primary/20 blur-xl" />
                          <div className="relative w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                            <span className="text-2xl font-black text-primary">
                              {team.abbreviation || team.short_name?.substring(0, 2) || team.name.substring(0, 2)}
                            </span>
                          </div>
                        </div>

                        {/* Team Info */}
                        <div>
                          <h3 className="text-xl font-bold text-white group-hover:text-primary transition-colors">
                            {team.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <MapPin className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-400">
                              {team.city}{team.state ? `, ${team.state}` : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                    </div>

                    {/* Quick Stats */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Division</span>
                        <span className="text-sm font-semibold text-white">
                          {team.division?.name || team.conference?.name || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Founded</span>
                        <span className="text-sm font-semibold text-white">
                          {team.founded_year || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Championships</span>
                        <span className="text-sm font-semibold text-primary">
                          {team.championships_won || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ModernTeamsPage;